import { EEthereumNetworkId } from '@ankr.com/provider';
import BigNumber from 'bignumber.js';
import config from 'config';
import { TransactionReceipt } from 'web3-core';
import { AbiItem } from 'web3-utils';

import { AutomateApi } from 'modules/api';
import { ErrorMessage, isEstimateFee, ZERO } from 'modules/common';
import {
  ContractsManager,
  convertToWei,
  getRegisterAbiItem,
  isWeb3KeyWriteProvider,
} from 'modules/core';
import { api, cacheTags } from 'modules/store';

export interface CreateTaskParams {
  accountAddress: string;
  chainId: EEthereumNetworkId;
  taskName: string;
  taskContractAddress: string;
  taskBalance?: BigNumber;
  gasLimit: BigNumber;
  abiItem: AbiItem;
  abi: AbiItem[];
  params: string[];
  cronExpression: string;
  startDate: Date;
  isCustomAbi: boolean;
}

const newCronTaskWithJobMultiplier = new BigNumber(1.5);

// https://ankrnetwork.atlassian.net/browse/AUTO-125
const ADDITIONAL_GAS_LIMIT = new BigNumber(150000);
export const { useCreateTaskMutation } = api.injectEndpoints({
  endpoints: build => ({
    createTask: build.mutation<TransactionReceipt, CreateTaskParams>({
      queryFn: async ({
        accountAddress,
        chainId,
        taskName,
        taskContractAddress,
        taskBalance = ZERO,
        gasLimit,
        abi,
        abiItem,
        params,
        cronExpression,
        startDate,
        isCustomAbi,
      }) => {
        if (!chainId) {
          throw new Error(ErrorMessage.ChainIdIsNotDefined);
        }

        const networkConfig = config[chainId];
        if (!networkConfig) {
          throw new Error(ErrorMessage.ConfigIsNotDefined);
        }

        const sdk = await ContractsManager.getInstance();
        const provider = await sdk.getProvider();
        if (!isWeb3KeyWriteProvider(provider)) {
          throw new Error(ErrorMessage.ProviderIsNotValid);
        }

        const web3 = provider.getWeb3();

        const instance = new AutomateApi(chainId);
        if (isCustomAbi) {
          try {
            // TODO: we cant upload custom abi twice (backend side feature)
            // issue: https://ankr.slack.com/archives/C03T80111U7/p1674037056531549
            await instance.uploadAbi(taskContractAddress, abi);
            // eslint-disable-next-line no-empty
          } catch {}
        }

        const encodedFunctionCall = web3.eth.abi.encodeFunctionCall(
          abiItem,
          params,
        );

        const cronUpkeepFactoryContract =
          await sdk.getCronTaskFactoryContract();

        const encodedJob = await cronUpkeepFactoryContract.methods
          .encodeCronJob(
            taskContractAddress,
            encodedFunctionCall,
            cronExpression,
          )
          .call({
            from: accountAddress,
          });

        const newCronTaskWithJobInfo = await sdk.getGasInfo({
          contract: cronUpkeepFactoryContract,
          method: 'newCronTaskWithJob',
          args: [encodedJob],
          currentAccount: accountAddress,
          multiplier: newCronTaskWithJobMultiplier,
        });

        const newCronTaskWithJobData = cronUpkeepFactoryContract.methods
          .newCronTaskWithJob(encodedJob)
          .encodeABI();

        const { contracts } = networkConfig;
        const estimateFee = isEstimateFee(chainId);
        const result = await provider.sendTransactionAsync(
          accountAddress,
          contracts.cronTaskFactory,
          {
            estimateFee,
            data: newCronTaskWithJobData,
            gasLimit: newCronTaskWithJobInfo.gasLimit.toString(),
          },
        );

        const receipt = await result.receiptPromise;

        const events = await sdk.getEventsFromReceipt(
          cronUpkeepFactoryContract,
          'NewCronTaskCreated',
          receipt.transactionHash,
        );

        if (!events.length) {
          throw new Error('NewCronUpkeepCreated is not found');
        }

        const {
          returnValues: { task: cronTaskContractAddress },
        } = events[0];

        const registrarContract = await sdk.getRegistrarContract();
        const registerAbi = getRegisterAbiItem();

        const encodedRegisterCall = web3.eth.abi.encodeFunctionCall(
          registerAbi,
          [
            taskName,
            '0x1234', // encoded email
            cronTaskContractAddress,
            gasLimit.plus(ADDITIONAL_GAS_LIMIT).toString(),
            accountAddress,
            '0x', // check data
            convertToWei(taskBalance),
            String(0), // source ( chain id ? )
            accountAddress,
            String(Math.round(startDate.getTime() / 1000)), // unix timestamp in seconds
          ],
        );

        const data = registrarContract.methods
          .transferWithData(encodedRegisterCall)
          .encodeABI();

        const transferWithDataInfo = await sdk.getGasInfo({
          contract: registrarContract,
          method: 'transferWithData',
          args: [encodedRegisterCall],
          value: taskBalance,
          currentAccount: accountAddress,
        });

        const { receiptPromise } = await provider.sendTransactionAsync(
          accountAddress,
          contracts.registrar,
          {
            data,
            estimateFee,
            gasLimit: transferWithDataInfo.gasLimit.toString(),
            value: convertToWei(taskBalance),
          },
        );

        return { data: await receiptPromise };
      },
      invalidatesTags: [cacheTags.taskData],
    }),
  }),
});
